<template>
  <fin-data-loader :resolver="fetchData" class="fin-screener-mask" fullpage>
    <template #loading_additional>
      <Portal :to="'breadcrumb_' + screenerType + 'Edit'">
        Lade...
      </Portal>
    </template>
    <template #content>

      <Portal v-if="screenerModel"
              :to="'breadcrumb_' + screenerType + 'Edit'">
        {{ screenerModel.name }}
      </Portal>

      <div class="header px-12 pt-12">
        <h1>{{ screenerModel.name }}</h1>
      </div>

      <div class="fin-screener-filters pa-3 pa-sm-12 pb-sm-0 mt-3" style="background-color: #ffffff;">
        <fin-subscription-content v-if="screenerModel.inactive"
                                  :feature-id="isShareScreener ? featureIds.SHARE_SCREENER.id : featureIds.ETF_SCREENER.id"
                                  :restriction-type="featureRestrictions.UNAVAILABLE"
                                  style="max-width: 700px;"/>

        <div v-if="!screenerModel.inactive" class="first-filter-step pb-6 pb-sm-12">
          <v-row>
            <v-col :md="isShareScreener ? 12 : 5" cols="12">
              <div class="fin-screener-content-header-number">1</div>
              <div class="fin-screener-content-header">
                {{ firstFiltersTitle }}
              </div>

              <v-row class="mt-3">
                <v-col v-for="(firstFilterStepEntry, firstFilterStepIndex) in firstFilterStepEntries"
                       :key="firstFilterStepIndex"
                       :md="firstFilterStepEntries.length === 1 ? 12 : 4" class="first-filter-step-col"
                       cols="12">
                  <!-- single select => object with an id -->
                  <fin-screener-filter-step
                    v-if="!firstFilterStepEntry.multiSelect && allSecondLevelFilter.length > 0 && getFirstLevelModel(firstFilterStepEntry.key)"
                    v-model="getFirstLevelModel(firstFilterStepEntry.key).id"
                    :items="firstFilterStepEntry.categories"
                    :multi-select="firstFilterStepEntry.multiSelect"
                    :selectTitle="isShareScreener ? firstFilterStepEntry.title : ''"
                    itemValue="key"
                    itemText="title"
                    @input="updateSecondLevelEntries"/>
                  <p v-else>Die Kriterien konnten nicht geladen werden</p>
                </v-col>
              </v-row>
            </v-col>

            <v-col v-if="isEtfScreener && $vuetify.breakpoint.mdAndUp" cols="12" md="7">
              <div class="fin-screener-content-header-number">3</div>
              <div class="fin-screener-content-header">
                Welche Spalten möchtest du anzeigen?
              </div>

              <v-expansion-panels class="mt-5" dense flat>
                <v-expansion-panel style="border-radius: 0">
                  <v-expansion-panel-header class="screener-result-panel-header pa-0">
                    <p v-style:color="$finColors.primaryDark" class="mb-0"
                       style="font-weight: bold; display: contents; font-size: 14px;">Anzeige anpassen</p>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="screener-result-panel-content">
                    <!-- multi-select to choose columns to display in results of etf screener => array of strings -->
                    <fin-screener-dropdown-selector v-if="etfScreenerColumns && etfScreenerColumns.categories"
                                                    :items="etfScreenerColumns.categories"
                                                    :label="etfScreenerColumns.title"
                                                    :show-remove-icon="false"
                                                    :single-select="false"
                                                    :value="screenerModel.resultColumns.map(item => getResultColumn(item))"
                                                    active-filter-prepend-label="Ausgewählte Spalten"
                                                    check-attribute="colId"
                                                    remove-all-active-filters-confirm-text="Möchtest du alle Spalten entfernen?"
                                                    remove-all-active-filters-confirm-title="Spalten entfernen"
                                                    @input="val => screenerModel.resultColumns = val.map(item => {return item.colId})"/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </div>

        <div v-if="!screenerModel.inactive" class="second-filter-step">
          <v-row class="px-3">
            <v-col :style="$vuetify.breakpoint.smAndUp ? 'max-width: 32px; min-width: 32px' : ''"
                   class="px-0"
                   cols="1">
              <div class="fin-screener-content-header-number">2</div>
            </v-col>
            <v-col cols="11">
              <div class="fin-screener-content-header">
                {{ secondFiltersTitle }}
              </div>
            </v-col>
          </v-row>

          <v-row v-if="secondFilterStepEntries && !screenerModel.inactive">
            <v-col cols="12" class="py-0" v-if="filterIsDisabled"
                   style="display: flex; align-items: center">
              <v-btn small text @click="openAboDialog">
                <v-icon icon
                        color="fin-color-primary-dark">
                  mdi-lock
                </v-icon>
              </v-btn>
              <p class="pa-0 ma-0">Mit einem Abo-Upgrade kannst du alle Filterkriterien nutzen!</p>
            </v-col>
            <v-col cols="12" md="5" class="fin-second-filter-step-col">
              <div ref="finScreenerSecondFilterStep" class="fin-screener-second-filter-step">
                <fin-screener-filter-step
                  :items="allSecondLevelFilter"
                  :item-text="$vuetify.breakpoint.xsOnly ? 'shortName' : 'name'"
                  :reset-after-change="true"
                  :return-object="true"
                  item-value="itemId"
                  label="Kriterium auswählen"
                  @input="checkEntryViaAutocompleteField"/>
                <fin-screener-second-filter-step v-if="$vuetify.breakpoint.mdAndUp"
                                                 :items="secondFilterStepEntries"
                                                 :screenerType="screenerType"
                                                 v-model="selectedSecondLevelItems"/>
              </div>
            </v-col>

            <v-col cols="12" md="7">
              <div style="height: 56px" class="d-flex align-center justify-end mb-2">
                <v-btn :disabled="!hasChanged"
                       :elevation="0"
                       @click="resetAllFilters"
                       class="mr-4 px-0"
                       style="border-radius: 0; background-color: transparent;"
                       text>
                  <span class="pl-2 pr-2">Zurücksetzen</span>
                  <v-icon :color="hasChanged ? $finColors.primaryDark : ''">
                    icon-refresh
                  </v-icon>
                </v-btn>
                <v-btn :color="$finColors.primaryDark" dense
                       @click="()=>{$emit('applyTool')}"
                       class="start-button" ref="execButton"
                       style="border-radius: 0">
                  <span class="start-button-text" style="color:white">Anwenden</span>
                </v-btn>
              </div>

              <v-card class="fin-filter-card" outlined>
                <v-row class="py-6 px-12"
                       v-if="selectedSecondLevelItems.length <= 0">
                  <v-col cols="12" ref="pictureCol">
                    <p ref="pictureIntroText" style="font-size: 18px; color: #99a0a4">
                      Lege die Kriterien und den Wertebereich fest nachdem
                      dein Wertpapieruniversum ausgewertet werden
                      soll.
                    </p>
                    <!-- key needed to make sure the image is redrawn when the window's size is changed -->
                    <v-img :height="maxHeightPicture"
                           :key="maxHeightPicture"
                           :src="require('@/assets/tools/screener-card.svg')"
                           contain
                           style="display: block; margin-left: auto; margin-right: auto;"
                    />
                  </v-col>
                </v-row>

                <v-row v-else class="fin-scroll ml-3" style="position:relative;">
                  <v-col class="pt-3 d-flex justify-end" cols="12">
                    <v-btn :elevation="0" class="fin-remove-all-filters"
                           @click="resetSecondLevelFilters"
                           color="transparent">
                      Alle entfernen
                    </v-btn>
                  </v-col>
                  <v-col class="py-12 px-8" cols="12">
                    <div
                      :key="selectedSecondLevelItem.item.id + '_' + selectedSecondLevelItem.item.itemId + '_' + index"
                      :style="$vuetify.breakpoint.xsOnly ? 'margin-bottom: 90px' : 'margin-bottom: 50px'"
                      v-for="(selectedSecondLevelItem, index) in selectedSecondLevelItems">
                      <fin-lexicon-text v-if="$vuetify.breakpoint.smAndUp" :text="selectedSecondLevelItem.item.name"
                                        auto-solve class="filter-name ma-0 pt-3"/>
                      <fin-lexicon-text v-else :text="selectedSecondLevelItem.item.shortName" auto-solve
                                        class="filter-name ma-0 pt-3"/>

                      <fin-screener-data-slider v-if="isShareScreener && selectedSecondLevelItem.item.displayType !== 'DROPDOWN'"
                                                screener-type="shareScreener"
                                                :marketCap="shareScreenerMarketCap.id"
                                                :position="selectedSecondLevelItem.item"
                                                :region="shareScreenerRegion.id"
                                                :sector="shareScreenerSector.id"
                                                :value="[selectedSecondLevelItem.min, selectedSecondLevelItem.max]"
                                                @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, true, false, false)"
                                                @input=" val => {selectedSecondLevelItem.min=val[0], selectedSecondLevelItem.max=val[1]}"/>
                      <fin-screener-dropdown-selector
                        v-else-if="isShareScreener && selectedSecondLevelItem.item.displayType === 'DROPDOWN'"
                        label="Auswählen"
                        :value="selectedSecondLevelItem.values"
                        :single-select="selectedSecondLevelItem.singleSelect"
                        :items="getSecondFilterFilterValuesItemsDefById(selectedSecondLevelItem.item.id)"
                        @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, true, true, false)"
                        @input="val => selectedSecondLevelItem.values = val"/>
                      <fin-screener-data-slider
                        v-if="isEtfScreener && selectedSecondLevelItem.item.displayType === 'HISTOGRAM'"
                        screener-type="etfScreener"
                        :asset-class="assetClass.id"
                        :position="selectedSecondLevelItem.item"
                        :value="[selectedSecondLevelItem.min, selectedSecondLevelItem.max]"
                        @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, false, false, true)"
                        @input=" val => {selectedSecondLevelItem.min=val[0], selectedSecondLevelItem.max=val[1]}"/>
                      <fin-screener-dropdown-selector
                        v-else-if="isEtfScreener && selectedSecondLevelItem.item.displayType === 'DROPDOWN'"
                        label="Auswählen"
                        :value="selectedSecondLevelItem.values"
                        :single-select="selectedSecondLevelItem.singleSelect"
                        :items="getSecondFilterFilterValuesItemsDefById(selectedSecondLevelItem.item.id)"
                        @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, false, true, false)"
                        @input="val => selectedSecondLevelItem.values = val"/>
                      <fin-screener-checkboxes-selector
                        v-else-if="isEtfScreener && selectedSecondLevelItem.item.displayType === 'CHECKBOX'"
                        :label="selectedSecondLevelItem.item.name"
                        :value="selectedSecondLevelItem.values"
                        :items="getSecondFilterFilterValuesItemsDefById(selectedSecondLevelItem.item.id)"
                        @input="val => selectedSecondLevelItem.values = val"
                        @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, false, true, false)"/>
                      <fin-screener-radio-buttons-selector
                        v-else-if="isEtfScreener && selectedSecondLevelItem.item.displayType === 'RADIO'"
                        :label="selectedSecondLevelItem.item.name"
                        :value="selectedSecondLevelItem.values && selectedSecondLevelItem.values.length > 0 ? selectedSecondLevelItem.values[0] : allSecondLevelFilter.find(item => item.id === selectedSecondLevelItem.item.id).filterValues.values[0]"
                        :items=" getSecondFilterFilterValuesItemsDefById(selectedSecondLevelItem.item.id)"
                        @input="val => selectedSecondLevelItem.values = val ? [val] : [allSecondLevelFilter.find(item => item.id === selectedSecondLevelItem.item.id).filterValues.values[0]]"
                        @delete="removeSecondLevelFilter(selectedSecondLevelItem.item, false, true, false)"/>
                      <p v-else-if="false">
                        No UI component to display this item <br/>
                        {{ selectedSecondLevelItem }}
                      </p>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-if="isEtfScreener && $vuetify.breakpoint.smAndDown && !screenerModel.inactive" class="pt-6">
            <v-col cols="12" md="7">
              <div class="fin-screener-content-header-number">3</div>
              <div class="fin-screener-content-header">
                Welche Spalten möchtest du anzeigen?
              </div>

              <v-expansion-panels class="mt-5" dense flat>
                <v-expansion-panel style="border-radius: 0">
                  <v-expansion-panel-header class="screener-result-panel-header pa-0">
                    <p v-style:color="$finColors.primaryDark" class="mb-0"
                       style="font-weight: bold; display: contents; font-size: 14px;">Anzeige anpassen</p>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="screener-result-panel-content">
                    <!-- multi-select to choose columns to display in results of etf screener => array of strings -->
                    <fin-screener-dropdown-selector v-if="etfScreenerColumns && etfScreenerColumns.categories"
                                                    :items="etfScreenerColumns.categories"
                                                    :label="etfScreenerColumns.title"
                                                    :show-remove-icon="false"
                                                    :single-select="false"
                                                    :value="screenerModel.resultColumns.map(item => getResultColumn(item))"
                                                    active-filter-prepend-label="Ausgewählte Spalten"
                                                    check-attribute="colId"
                                                    remove-all-active-filters-confirm-text="Möchtest du alle Spalten entfernen?"
                                                    remove-all-active-filters-confirm-title="Spalten entfernen"
                                                    @input="val => screenerModel.resultColumns = val.map(item => {return item.colId})"/>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </div>
      </div>

      <div class="bottom-filter px-6 px-sm-12 pb-7 pt-3 pt-md-3"
           v-if="!screenerModel.inactive"
           style="background-color: white">
        <v-divider v-if="$vuetify.breakpoint.smAndDown"/>
        <v-row class="d-flex align-center mt-0">
          <v-col cols="12" lg="8" sm="5">
            <div class="accordance">
              <span style="font-weight: bold; font-size: 18px">Übereinstimmung: </span>
              <div style="display: inline-block">
                <span style="font-weight: bold; font-size: 18px">
                  {{ itemsFound }}
                </span>
                <span style="font-size: 16px;">
                  von {{ itemsTotal }} Wertpapieren
                </span>
              </div>
            </div>
          </v-col>
          <v-col style="text-align: right;">
            <v-btn :disabled="!hasChanged"
                   @click="resetAllFilters"
                   class="mr-4 px-0"
                   style="border-radius: 0; background-color: transparent;"
                   text>
              <span class="pl-2 pr-2">Zurücksetzen</span>
              <v-icon :color="hasChanged ? $finColors.primaryDark : ''">
                icon-refresh
              </v-icon>
            </v-btn>
            <v-btn :color="$finColors.primaryDark" dense
                   @click="()=>{$emit('applyTool')}"
                   class="start-button"
                   style="border-radius: 0">
              <span class="start-button-text" style="color:white">Anwenden</span>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </template>
  </fin-data-loader>
</template>

<script>
import FinScreenerFilterStep from '@/components/pages/finTools/finScreener/screenerMask/filterStep/index'
import FinScreenerSecondFilterStep from '@/components/pages/finTools/finScreener/screenerMask/secondFilterStep/index'
import {finEtfAPI, finScreenerAPI, finShareScreenerAPI, finEtfScreenerAPI, finShareAPI} from '@/js/finAPI'
import KeyFunctionMixin from '@/mixins/KeyFunction'
import FinDataLoader from '@/components/common/DataLoader/index'
import assetClassesMock from "@/mocks/AssetClassesScreener"
import FinScreenerDropdownSelector from "@/components/pages/finTools/finScreener/screenerMask/screenerDropdownSelector"
import FinScreenerCheckboxesSelector
  from "@/components/pages/finTools/finScreener/screenerMask/screenerCheckboxesSelector"
import FinScreenerDataSlider from "@/components/pages/finTools/finScreener/screenerMask/screenerDataSlider"
import FinScreenerRadioButtonsSelector
  from "@/components/pages/finTools/finScreener/screenerMask/screenerRadioButtonsSelector"
import FinLexiconText from "@/components/common/lexicon/LexiconText"
import perfectscrollbar from "fincraft-lib-basics/src/mixins/perfectscrollbar"
import PriceModel from "@/mixins/PriceModel"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import {EventBus} from "@/plugins/fincraft"
import FinSubscriptionContent from "@/components/common/subscription/content"

export default {
  name: 'fin-screener-content',
  components: {
    FinSubscriptionContent,
    FinLexiconText,
    FinScreenerRadioButtonsSelector,
    FinScreenerDataSlider,
    FinScreenerCheckboxesSelector,
    FinScreenerDropdownSelector,
    FinDataLoader,
    FinScreenerSecondFilterStep,
    FinScreenerFilterStep,
  },
  mixins: [
    KeyFunctionMixin,
    perfectscrollbar,
    PriceModel
  ],
  props: {
    value: {
      type: Object
    },
    hasChanged: {
      type: Boolean,
      default: false
    },
    // screenerType values: 'shareScreener' or 'etfScreener'
    screenerType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      firstFilterStepEntries: [],
      secondFilterStepEntries: null,
      groups: null,
      itemsFound: 0,
      itemsTotal: 0,
      debug: false,
      maxHeightPicture: 200,
      contentReady: false,
      perfectScrollbarOpts: {
        suppressScrollX: true
      },
      shareScreenerType: 'shareScreener',
      etfScreenerType: 'etfScreener',
      isShareScreener: this.screenerType === 'shareScreener',
      isEtfScreener: this.screenerType === 'etfScreener',
      firstFiltersTitle: this.screenerType === 'shareScreener' ?
        'Welches Wertpapieruniversum willst du durchsuchen?' : this.screenerType === 'etfScreener' ?
          'Welches ETF-Universum willst du durchsuchen?' : '',
      secondFiltersTitle: this.screenerType === 'shareScreener' ?
        'Wähle Kriterien aus und setze dann den gewünschten Wertebereich' : this.screenerType === 'etfScreener' ?
          'Wähle Suchkriterien aus und setze die gewünschten Werte' : '',
      etfScreenerColumns: undefined,
      screenerFiltersItems: [],
      selectedSecondLevelItems: undefined,
      featureIds: PRICEMODEL_FEATURE_IDS,
      featureRestrictions: PRICEMODEL_FEATURE_RESTRICTIONS,
      lastHits: 0
    }
  },
  computed: {
    screenerModel: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
        this.lastHits = newVal.lastHits
      }
    },
    filterIsDisabled() {
      let disabledFilter = this.allSecondLevelFilter.find(secondLevelFilter => secondLevelFilter.disabled === true)
      return disabledFilter !== undefined
    },
    priceModelFeatureId() {
      if (this.isEtfScreener) {
        return PRICEMODEL_FEATURE_IDS.ETF_SCREENER.id
      } else {
        return PRICEMODEL_FEATURE_IDS.SHARE_SCREENER.id
      }
    },
    availableFilters() {
      if (!this.secondFilterStepEntries) return []
      const filtersByPriceModel = this.priceModelEnabledAndLevels(this.priceModelFeatureId)

      if (filtersByPriceModel.length === 0 || filtersByPriceModel[0].id === 'SCREENER_ETF_UNLIMITED' || filtersByPriceModel[0].id === 'SCREENER_SHARE_UNLIMITED') {
        const allFilters = []
        this.secondFilterStepEntries.categories.forEach(cat => cat.categories.forEach(filter => allFilters.push(filter.id)))
        return allFilters
      }

      return filtersByPriceModel.reduce((acc, item) => {
        const cat = this.secondFilterStepEntries.categories.find(cat => cat.key === item.id)
        if (cat) {
          cat.categories.forEach(filter => acc.push(filter.id))
        }
        return acc
      }, [])
    },
    allSecondLevelFilter() {
      const result = []

      if (this.groups && this.groups.entries) {
        this.groups.entries.items.forEach(posgroup => {
          if (posgroup.isSubGroup) {
            posgroup.subPosGroup.entries.items.forEach(subPosGroup => {
              if (!subPosGroup.isSubGroup) {
                result.push(subPosGroup.dataItem)
              }
            })
          }
        })

        result.map((item) => {
          item.disabled = !this.categoryIsEnabled(item.id)
        })
      }
      return result.sort((a, b) => a.name.localeCompare(b.name))
    },
    shareScreenerRegion() {
      // region only used for share screener.
      return this.screenerModel.region
    },
    shareScreenerMarketCap() {
      // market cap only used for share screener.
      return this.screenerModel.marketCapCategory
    },
    shareScreenerSector() {
      // sector only used for share screener.
      return this.screenerModel.sector
    },
    assetClass() {
      // assetClass only used for etf screener.
      return this.screenerModel.assetClass
    }
  },
  methods: {
    openAboDialog() {
      EventBus.$emit('showSubscriptionPopup', this.priceModelFeatureId, PRICEMODEL_FEATURE_RESTRICTIONS.QUALITY)
    },
    categoryIsEnabled(item) {
      return this.availableFilters.includes(item)
    },
    getScrollElement() {
      return this.$el.querySelector('.fin-scroll', this.perfectScrollbarOpts)
    },
    getResultColumn(item) {
      const id = item.indexOf(':') === -1 ? item : item.substr(0, item.indexOf(':'))
      const col = this.etfScreenerColumns.categories.find(cat => cat.id === id)
      const name = col ? col.name : 'unbekannt'

      const itemId = item.indexOf(':') === -1 ? item : item.substr(item.indexOf(':') + 1)
      const colId = id === itemId ? id : id + ":" + itemId
      return {id, colId, itemId, name}
    },
    getSecondFilterDefById(id) {
      return this.allSecondLevelFilter.find(item => item.id === id)
    },
    getSecondFilterFilterValuesItemsDefById(id) {
      const secondFilter = this.getSecondFilterDefById(id)
      return secondFilter && secondFilter.filterValues ? secondFilter.filterValues.values : []
    },
    async fetchData() {
      if (!this.value.inactive) {
        if (this.isShareScreener) {
          this.firstFilterStepEntries = await finShareScreenerAPI.firstFilterStepShareScreenerEntries()
          this.groups = await finScreenerAPI.screenerPositionGroups('SHARE')
          this.secondFilterStepEntries = this.getSecondFilterStepEntries()
        } else if (this.isEtfScreener) {
          this.groups = await finScreenerAPI.screenerPositionGroups(this.assetClass.id)
          this.secondFilterStepEntries = this.getSecondFilterStepEntries()
        }

        // adapt valuesFilter values (in backend: Array of strings, in frontend: Array of objects)
        this.screenerModel.valueFilters.items.forEach(valueFilterItem => {
          const filterValuesItem = this.getSecondFilterDefById(valueFilterItem.item.id)
          const valuesDefined = filterValuesItem?.filterValues?.values
          if (valuesDefined) {
            valueFilterItem.values = valueFilterItem.values.map(value => {
              const defValue = valuesDefined.find(valueDef => valueDef.id === value.id)
              return {
                ...value,
                name: defValue ? defValue.name : 'unbekannt'
              }
            })
            // if a screener contains a value that has been removed in the meantime in the backend, ignore this value
            valueFilterItem.values = valueFilterItem.values.filter(item => item.name !== 'unbekannt')
          }
        })

        if (this.isShareScreener) {
          this.itemsTotal = await finShareAPI.totalShares()
          await this.refreshResults()
        } else if (this.isEtfScreener) {
          const etfAssetClasses = assetClassesMock.items.filter(item => item.id !== 'SHARE')
          this.firstFilterStepEntries = [
            {
              key: 'assetClass',
              title: 'Assetklasse wählen',
              categories: etfAssetClasses.map(x => {
                return {
                  key: x.id,
                  title: x.name
                }
              })
            }
          ]
          this.etfScreenerColumns = {
            key: 'resultColumns',
            title: 'Spalten wählen',
            multiSelect: true,
            categories: this.allSecondLevelFilter.map(x => {
              return {
                id: x.id,
                itemId: x.itemId,
                colId: x.id + ":" + x.itemId,
                name: x.name
              }
            })
          }
          this.itemsTotal = await finEtfAPI.totalEtfs(this.screenerModel.assetClass.id)
          await this.refreshResults()
        }
      }
    },
    async saveScreener(val) {
      try {
        // check for unique value filters
        this.removeDuplicateFilterItems(val.valueFilters)
        // check for unique range filters
        if (this.isShareScreener) {
          this.removeDuplicateFilterItems(val.filters)
        } else if (this.isEtfScreener) {
          this.removeDuplicateFilterItems(val.rangeFilters)
        }

        // check abo
        if (this.isPriceModelFeatureEnabled(this.priceModelFeatureId)) {
          console.log("saveScreener", val)
          // clone the screener object into cloned and set the userId
          const cloned = JSON.parse(JSON.stringify(val))
          if (this.isShareScreener) {
            await finShareScreenerAPI.saveShareScreenerConfiguration(cloned)
          } else if (this.isEtfScreener) {
            await finEtfScreenerAPI.saveEtfScreenerConfiguration(cloned)
          }
        } else {
          console.log("the user has an abo, which does not allow the share/etf screener")
        }
      } catch (ex) {
        //TODO handle
        console.error('failed to save modified screener configuration', ex)
      }
    },
    hasHistogramData(item) {
      return item.histograms && item.histograms.items && item.histograms.items.length > 0
    },
    removeSecondLevelFilter(entry, isShareScreenerFilter, isEtfScreenerValueFilter, isEtfScreenerRangeFilter) {
      if (isShareScreenerFilter) {
        this.screenerModel.filters.items = this.screenerModel.filters.items.filter(filter => {
          return filter.item.itemId !== entry.itemId
        })
      } else if (isEtfScreenerValueFilter) {
        this.screenerModel.valueFilters.items = this.screenerModel.valueFilters.items.filter(filter => {
          return filter.item.id !== entry.id // TODO needed? && filter.item.itemId !== entry.itemId
        })
      } else if (isEtfScreenerRangeFilter) {
        this.screenerModel.rangeFilters.items = this.screenerModel.rangeFilters.items.filter(filter => {
          return filter.item.id !== entry.id // TODO needed? && filter.item.itemId !== entry.itemId
        })
      }

      this.selectedSecondLevelItems = this.selectedSecondLevelItems.filter(ssli => ssli.item.id !== entry.id)
    },
    getFirstLevelModel(key) {
      let result
      for (const keyModel in this.screenerModel) {
        if (key === keyModel) {
          result = this.screenerModel[key]
          break
        }
      }
      return result ? result : []
    },
    getSecondFilterStepEntries() {
      const result = {
        key: 'kriterien',
        title: 'Kriterien',
        categories: []
      }

      if (this.groups && this.groups.entries) {
        this.groups.entries.items.forEach(posgroup => {
          if (posgroup.isSubGroup) {
            const category = {
              key: posgroup.subPosGroup.groupId,
              title: posgroup.subPosGroup.name,
              shortName: posgroup.subPosGroup.shortName,
              categories: []
            }

            if (posgroup.subPosGroup.entries.items) {
              posgroup.subPosGroup.entries.items.forEach(subPosGroup => {
                if (!subPosGroup.isSubGroup) {
                  const dataItem = {
                    id: subPosGroup.dataItem.id,
                    itemId: subPosGroup.dataItem.itemId,
                    key: subPosGroup.dataItem.itemId,
                    title: subPosGroup.dataItem.name,
                    shortName: subPosGroup.dataItem.shortName ? subPosGroup.dataItem.shortName : subPosGroup.dataItem.name,
                    unit: subPosGroup.dataItem.unit,
                    displayType: subPosGroup.dataItem.displayType
                  }
                  if (subPosGroup.dataItem.filterValues) {
                    dataItem.filterType = 'valuesFilter'
                    dataItem.filterValues = subPosGroup.dataItem.filterValues
                    if (dataItem.filterValues.values) {
                      dataItem.filterValues.values.sort((a, b) => a.orderNumber - b.orderNumber)
                    }
                  } else if (this.hasHistogramData(subPosGroup.dataItem)) {
                    dataItem.filterType = 'rangeFilter'
                    dataItem.minValue = subPosGroup.dataItem.minValue
                    dataItem.maxValue = subPosGroup.dataItem.maxValue
                    dataItem.histograms = subPosGroup.dataItem.histograms
                  }
                  category.categories.push(dataItem)
                }
              })
              // add category with items only
              if (category.categories.length > 0) {
                result.categories.push(category)
              }
            }
          }
        })
      }

      return result
    },
    async updateSecondLevelEntries() {
      if (this.isEtfScreener) {
        const newGroups = await finScreenerAPI.screenerPositionGroups(this.assetClass.id)

        if (newGroups) {
          this.groups = newGroups
          this.secondFilterStepEntries = this.getSecondFilterStepEntries()
          // reset selectedSecondLevelItems + screenerModel (which triggers a save)
          // because each asset class has its own config options / groups
          this.selectedSecondLevelItems = []
          this.screenerModel.rangeFilters.items = []
          this.screenerModel.valueFilters.items = []
          // update the selectable columns based on asset class (= allSecondLevelFilter)
          this.etfScreenerColumns.categories = this.allSecondLevelFilter.map(x => {
            return {
              id: x.id,
              itemId: x.itemId,
              colId: x.id + ":" + x.itemId,
              name: x.name
            }
          })

          // reset resultColumns to default after asset class change
          this.screenerModel.resultColumns = await this.getScreenerResultColumns(this.assetClass.id)
        }
      }
    },
    /** get default columns from backend */
    async getScreenerResultColumns(assetClassId) {
      // Stand 03.06.2022: getScreenerResultColumns returns Array of Strings,
      // where the strings are the ids of the criteria.
      // In the future it could also return id:itemId instead.
      // => handling both
      const columns = await finScreenerAPI.getScreenerResultColumns(assetClassId)
      let itemIdsArray = []
      columns.forEach(column => {
        if (column.indexOf(':') === -1) {
          // when column = id, get itemId from the definitions of the criteria (ids are unique)
          const col = this.etfScreenerColumns.categories.find(cat => cat.id === column)
          const itemId = col.itemId
          itemIdsArray.push(column + ':' + itemId)
        } else {
          // when column = id:itemId
          itemIdsArray.push(column)
        }
      })
      return itemIdsArray
    },
    checkEntryViaAutocompleteField(entry) {
      // is the entry currently selected? If so, deselect it; otherwise select it and set default values
      if (entry) {
        // handle share screener
        if (this.isShareScreener) {
          let valueFilterFound = this.screenerModel.valueFilters.items.find(filter => filter.item.id === entry.id)
          let rangeFilterFound = this.screenerModel.filters.items.find(filter => filter.item.id === entry.id)
          if (valueFilterFound || rangeFilterFound) {
            this.removeSecondLevelFilter(entry, !!valueFilterFound, false, !!rangeFilterFound)
          } else {
            let filter
            if (entry.filterValues && entry.filterValues.values && entry.filterValues.values.length > 0) {
              filter = {
                item: entry,
                filterType: 'valuesFilter',
                // select none by default
                values: [] //entry.filterValues && entry.filterValues.values ? entry.filterValues.values : []
              }
              this.screenerModel.valueFilters.items.push(filter)
            } else if (this.hasHistogramData(entry)) {
              filter = {
                item: entry,
                filterType: 'rangeFilter',
                min: null,
                max: null
              }
              this.screenerModel.filters.items.push(filter)
            }
            this.selectedSecondLevelItems.unshift(filter)
          }
        }
        // handle etf screener
        else if (this.isEtfScreener) {
          let valueFilterFound = this.screenerModel.valueFilters.items.find(filter => filter.item.id === entry.id)
          let rangeFilterFound = this.screenerModel.rangeFilters.items.find(filter => filter.item.id === entry.id)
          if (valueFilterFound || rangeFilterFound) {
            this.removeSecondLevelFilter(entry, false, !!valueFilterFound, !!rangeFilterFound)
          } else {
            let filter
            if (entry.filterValues && entry.filterValues.values && entry.filterValues.values.length > 0) {
              filter = {
                item: entry,
                filterType: 'valuesFilter',
                // select none by default
                values: [] //entry.filterValues && entry.filterValues.values ? entry.filterValues.values : []
              }
              this.screenerModel.valueFilters.items.push(filter)
            } else if (this.hasHistogramData(entry)) {
              filter = {
                item: entry,
                filterType: 'rangeFilter',
                min: null,
                max: null
              }
              this.screenerModel.rangeFilters.items.push(filter)
            }
            this.selectedSecondLevelItems.unshift(filter)
          }
        } else {
          console.error('checkEntryViaAutocompleteField: no such screener type', this.screenerType)
        }
      }
    },
    resetAllFilters() {
      this.$emit('reset')
      this.selectedSecondLevelItems = []
    },
    resetSecondLevelFilters() {
      if (this.isShareScreener) {
        this.screenerModel.filters.items = []
      } else if (this.isEtfScreener) {
        this.screenerModel.rangeFilters.items = []
      }
      this.screenerModel.valueFilters.items = []
      this.selectedSecondLevelItems = []
    },
    async refreshResults() {
      if (this.isShareScreener) {
        this.itemsFound = await finShareAPI.secondFilterShareScreenerSharesTotal(this.screenerModel) | 0
      } else if (this.isEtfScreener) {
        this.itemsFound = await finEtfAPI.secondFilterEtfScreenerEtfsTotal(this.screenerModel) | 0
        this.itemsTotal = await finEtfAPI.totalEtfs(this.screenerModel.assetClass.id)
      }

      if (this.lastHits !== this.itemsFound) {
        this.lastHits = this.itemsFound
        if (this.isShareScreener) {
          await finShareScreenerAPI.updateShareScreenerHits(this.screenerModel.id, this.itemsFound)
        } else if (this.isEtfScreener) {
          await finEtfScreenerAPI.updateEtfScreenerHits(this.screenerModel.id, this.itemsFound)
        }
      }
    },
    removeDuplicateFilterItems(filter){
      if (filter && filter.items && filter.items.length > 1) {
        for (let i = filter.items.length - 1; i > 0; i--) {
          // id is identifier of the field (FinancialDataItem.id)
          // itemId is the used field and might be used for multiple position id (FinancialDataItem.itemId)!
          const positionId = filter.items[i].item.id
          if ( i > filter.items.findIndex((item) => {
             return item.item.id === positionId
          })) {
             console.error('removed duplicate for ', i, positionId)
             filter.items.splice(i, 1)
          }
        }
      }
    }
  },
  watch: {
    value: {
      async handler(val) {
        if (!val.inactive) {
          // do not call saveScreener right after initializing the value!
          if (this.contentReady === true) {
            await this.saveScreener(val)
          } else {
            console.log("screener not saved")
          }
          this.screenerModel = val
        }
      },
      deep: true
    },
    screenerModel: {
      async handler() {
        if (!this.value.inactive) {
          await this.refreshResults()
        }
      },
      deep: true
    },
    selectedSecondLevelItems: {
      handler(values) {
        if (values.length === 0) {
          // if there are no more selected criteria, set the filters to [] in the screenerModel
          if (this.isShareScreener) {
            this.screenerModel.filters.items = []
            this.screenerModel.valueFilters.items = []
          } else if (this.isEtfScreener) {
            this.screenerModel.rangeFilters.items = []
            this.screenerModel.valueFilters.items = []
          }
        } else {
          // if there is at least one selected criteria, set it or update it in the screenerModel
          values.forEach(val => {
            if (this.isShareScreener && val.filterType === 'rangeFilter') {
              let existingFilter = this.screenerModel.filters.items.find(rf => rf.item.id === val.item.id)

              if (existingFilter === undefined) {
                this.screenerModel.filters.items.push(val)
              } else {
                existingFilter = val
              }
            } else if (this.isEtfScreener && val.filterType === 'rangeFilter') {
              let existingFilter = undefined
              if (this.screenerModel.rangeFilters) {
                existingFilter = this.screenerModel.rangeFilters?.items.find(rf => rf.item.id === val.item.id)
              }

              if (existingFilter === undefined) {
                this.screenerModel.rangeFilters?.items.push(val)
              } else {
                existingFilter = val
              }
            } else if (val.filterType === 'valuesFilter') {
              let existingFilter = this.screenerModel.valueFilters.items.find(rf => rf.item.id === val.item.id)
              if (existingFilter === undefined) {
                // set first radio button element as selected (see screenerRadioButtonSelector)
                if (val.item.displayType === 'RADIO') {
                  let radioButtonValues = this.secondFilterStepEntries.categories.find(c => c.categories.find(cc => cc.displayType === 'RADIO' && cc.id === val.item.id))

                  if (radioButtonValues) {
                    radioButtonValues = radioButtonValues.categories.find(rbv => rbv.displayType === 'RADIO' && rbv.id === val.item.id)

                    if (radioButtonValues) {
                      val.values.push(radioButtonValues.filterValues.values[0])
                    }
                  }
                }
                this.screenerModel.valueFilters.items.push(val)
              } else {
                existingFilter = val
              }
            }
          })

          // if a criteria has been removed, remove it in the filters of the screenerModel too
          this.removeDuplicateFilterItems(this.screenerModel.valueFilters)

          if (this.isShareScreener) {
            this.removeDuplicateFilterItems(this.screenerModel.filters)
          } else if (this.isEtfScreener) {
            this.removeDuplicateFilterItems(this.screenerModel.rangeFilters)
          }
        }
      },
      deep: true,
    }
  },
  mounted() {
    // after loading the page, the filter type is not known and has to be set for each criteria in the filters
    const elements = (this.screenerType === 'shareScreener' ? this.value.filters.items : this.value.rangeFilters.items).concat(this.value.valueFilters.items)
    elements.forEach(e => {
      if ('values' in e) {
        e.filterType = 'valuesFilter'
      } else {
        e.filterType = 'rangeFilter'
      }
    })
    this.selectedSecondLevelItems = elements

    this.contentReady = true
  }
}
</script>

<style lang="scss" scoped>

.fin-screener-content {
  background-color: #F7F7F7;
}

.fin-screener-content-header {
  font-size: 20px;
  font-weight: bold;
  display: inline;
}

.fin-screener-content-header-number {
  width: 32px;
  height: 32px;
  text-align: center;
  line-height: 32px;
  color: white;
  background-color: $fin-color-primary-dark;
  border-radius: 50px;
  margin-right: 10px;
  font-size: 18px;
  font-weight: bold;
  float: left;
}

::v-deep .v-select__selection,
::v-deep .v-input .v-icon:before {
  color: $fin-color-primary-dark !important;
}

.fin-filters-buttons {
  position: absolute;
  top: 10px;
}

.fin-filter-card {
  transition: height .1s ease-in-out;

  & > .row {
    overflow-x: hidden;
    overflow-y: auto;
    height: inherit;
    top: 12px;
    left: -10px;
  }

  &::before {
    content: '';
    position: absolute;
    margin: auto;
    top: 90px;
    left: -12px;
    width: 25px;
    height: 25px;
    transform: rotate(315deg);
    -webkit-transform: rotate(315deg);
    -moz-transform: rotate(315deg);
    -o-transform: rotate(315deg);
    -ms-transform: rotate(315deg);
    border-right: 1px solid #E0E0E0;
    border-bottom: 1px solid #E0E0E0;
    background-color: white;
  }
}


::v-deep .v-expansion-panel-header, ::v-deep .v-expansion-panel--active, ::v-deep .v-expansion-panel-content {
  background-color: transparent !important;
}

::v-deep .v-expansion-panel--active:not(:first-child),
::v-deep .v-expansion-panel--active + .v-expansion-panel {
  margin-top: 0 !important;
}

.search-input .v-icon::before {
  color: $fin-color-primary-dark !important;
  opacity: 0.55;
}

.filter-name {
  font-weight: bold;
  font-size: 18px;
}

.screener-result-panel-header {
  min-height: 36px;
}

.screener-result-panel-header ::v-deep .v-expansion-panel-header__icon {
  margin-left: 12px;
}

.screener-result-panel-header ::v-deep .v-expansion-panel-header__icon i {
  color: $fin-color-primary-dark !important;
  font-weight: bold;
}

.screener-result-panel-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 20px 0 0;
}
</style>
